import { PipelineMoreMenu } from '@/features/adp/components/menus';
import { MaterialIcon } from '@/ui/material-icon';
import { Badge } from '@/ui/badge';
import { Button } from '@/ui/button';
import { Separator } from '@/ui/separator';
import { Skeleton } from '@/ui/skeleton';
import styles from './CanvasActionsMenu.module.scss';
import { useAppDispatch, useAppSelector } from '@/app/reduxHooks';
import { useSidebarTab } from '@/features/adp/hooks/useSidebarTab';
import { useAutoFillQuery } from '@/features/adp/hooks/useAutoFillQuery';
import { useCallback, useEffect } from 'react';
import { setPipelineDetails, setStatus } from '@/features/adp/state/pipelineSlice';
import { toast } from 'sonner';
import { useKillJobMutation, useRunImmediateJobMutation } from '@/features/adp/mutations/usePipelineMutations';
import { useWebSocketEvent } from '@/lib/websocket/useWebSocketEvent';
import { mapRawStatusToJobStatus } from '@/features/job-monitoring/utils/statusMapper';
import { STATUS_PILL_CFG } from './constants';
import { openOverlay } from '@/features/adp/state/overlaySlice';
import { setValidationResult, startValidating } from '@/features/adp/state/validationSlice';
import { selectCanvasNodes, selectCanvasEdges } from '@/features/adp/state/canvas/canvasSelectors';
import { getAdpSessionNode } from '@/features/adp/hooks/useAdpSessionStore';
import { validatePipeline } from '@/features/adp/utils/pipelineValidator';

interface CanvasActionsMenuProps {
  hasCanvasNodes: boolean;
  onLockPipeline: () => void;
  onClearCanvas: () => void;
  canvasLocked: boolean;
}

export default function CanvasActionsMenu({
  hasCanvasNodes,
  onLockPipeline,
  onClearCanvas,
  canvasLocked,
}: Readonly<CanvasActionsMenuProps>) {
  const { isRunEnabled, pipelineName, status, dataFlowId, sessionId, scheduleDetails } =
    useAppSelector(state => state.pipeline);
  const effectiveStatus = status === 'completed' ? 'finished' : status;
  const isRunningPipeline = effectiveStatus === 'running';
  const pillCfg = STATUS_PILL_CFG[effectiveStatus] ?? STATUS_PILL_CFG.idle;
  const isSpinning = effectiveStatus === 'initializing' || effectiveStatus === 'running';
  const isInitializingPipeline = effectiveStatus === 'initializing';
  const { tab: activeTab } = useSidebarTab();
  const dispatch = useAppDispatch();
  const runPipelineMutation = useRunImmediateJobMutation();
  const killJobMutation = useKillJobMutation();

  const reduxNodes = useAppSelector(selectCanvasNodes);
  const reduxEdges = useAppSelector(selectCanvasEdges);
  const validationResult = useAppSelector(state => state.validation.result);
  const isValidating = useAppSelector(state => state.validation.isValidating);
  const isValidationDirty = useAppSelector(state => state.validation.isDirty);

  const isValidated = Boolean(validationResult?.isValid) && !isValidationDirty && !isValidating;
  const validationErrorCount = validationResult?.summary.errors ?? 0;

  const { data, isPending, isSuccess } = useAutoFillQuery({
    baseName: 'My_ADP_Pipeline',
    tableName: 'data_flow',
    enabled: activeTab === 'assets',
  });

  useEffect(() => {
    const suggestedName = data?.suggestedName?.trim();
    if (isSuccess && suggestedName && !pipelineName?.trim()) {
      dispatch(
        setPipelineDetails({
          pipelineName: suggestedName,
          pipelineDescription: '',
        })
      );
    }
  }, [isSuccess, data, dispatch, pipelineName]);

  useWebSocketEvent('data_flow', event => {
    if (!dataFlowId) return;
    if (String(event.id) !== String(dataFlowId)) return;

    const mappedStatus = mapRawStatusToJobStatus(event.status ?? '');
    if (!mappedStatus) return;
    if (mappedStatus === 'connecting') return;

    if (mappedStatus === 'finished') {
      dispatch(setStatus('completed'));
      return;
    }
    if (mappedStatus === 'stopped') {
      dispatch(setStatus('stopped'));
      return;
    }

    dispatch(setStatus(mappedStatus));
  });

  const handleValidatePipeline = useCallback(() => {
    if (!hasCanvasNodes) {
      toast.error('Add at least one node to the pipeline before validating.');
      return;
    }

    dispatch(startValidating());
    const sessionStore: Record<string, Record<string, unknown>> = {};
    for (const node of reduxNodes) {
      const record = getAdpSessionNode(node.id);
      if (record) sessionStore[node.id] = record;
    }
    const result = validatePipeline({
      nodes: reduxNodes,
      edges: reduxEdges,
      sessionStore,
    });

    dispatch(setValidationResult(result));
    if (result.isValid) {
      toast.success('Pipeline validated. You can run it now.');
    } else {
      toast.error(
        `Validation found ${result.summary.errors} ${result.summary.errors === 1 ? 'issue' : 'issues'
        }. See the panel for details.`
      );
    }
  }, [dispatch, hasCanvasNodes, reduxEdges, reduxNodes]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isShortcut = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'e';
      if (!isShortcut) return;
      e.preventDefault();
      handleValidatePipeline();
    };
    globalThis.addEventListener('keydown', handleKeyDown);
    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, [handleValidatePipeline]);

  const handleRunPipeline = async () => {
    if (!sessionId) {
      toast.error('Unable to run pipeline. Please check your configuration.');
      return;
    }
    if (!isValidated) {
      toast.error('Validate the pipeline before running.');
      return;
    }
    try {
      await runPipelineMutation.mutateAsync(sessionId);
      dispatch(setStatus('initializing'));
      toast.success('Pipeline triggered successfully');
    } catch {
      toast.error('Unable to run pipeline. Please try again.');
      dispatch(setStatus('failed'));
    }
  };

  const handleEditPipeline = useCallback(() => {
    dispatch(
      openOverlay({
        type: 'edit-pipeline-modal',
        data: { dataFlowId: dataFlowId ?? null },
      })
    );
  }, [dataFlowId, dispatch]);

  const validateIconName = (() => {
    if (isValidating) return 'progress_activity';
    if (validationErrorCount > 0) return 'error';
    return 'check_circle';
  })();

  const validateButtonLabel = (() => {
    if (isValidating) return 'Validating…';
    if (isValidated) return 'Validated';
    if (validationErrorCount > 0) return `Validate (${validationErrorCount})`;
    return 'Validate';
  })();

  const isRunButtonDisabled =
    !isRunEnabled ||
    !isValidated ||
    isRunningPipeline ||
    isInitializingPipeline ||
    runPipelineMutation.isPending;

  const handleSchedulePipeline = () => {
    dispatch(
      openOverlay({
        type: 'schedule-pipeline-modal',
        data: {
          dataFlowId: dataFlowId ?? null,
          sessionId: sessionId ?? null,
          pipelineName: pipelineName ?? null,
          scheduleDetails: scheduleDetails ?? undefined,
          isSchedule: Boolean(scheduleDetails),
          persistScheduleInPipelineStore: true,
        },
      })
    );
  };

  const handleStopPipeline = async () => {
    if (!sessionId) {
      toast.error('Unable to stop pipeline. Please check your configuration.');
      return;
    }
    killJobMutation.mutate(sessionId, {
      onSuccess: () => {
        toast.success('Pipeline stopped successfully');
        dispatch(setStatus('stopped'));
      }
    });
  };

  return (
    <div className={styles.pipelineBar}>
      <div className={styles.pipelineInner}>
        {/* Actions dropdown */}
        {/* <PipelineActionsMenu
          triggerClassName={styles.actionsBtn}
        />

        <Separator orientation="vertical" className={styles.vbar} /> */}

        {/* Pipeline name */}
        {isPending && !pipelineName ? (
          <Skeleton style={{ width: 180, height: 28 }} aria-label="Pipeline name loading" />
        ) : (
          <button
            type="button"
            className={styles.pipelineName}
            onClick={handleEditPipeline}
            title="Edit pipeline name and description"
            aria-label={`Edit pipeline, ${pipelineName}`}
          >
            <span className={styles.pipelineNameText}>{pipelineName}</span>
            <MaterialIcon name="edit" size={14} aria-hidden />
          </button>
        )}

        <Separator orientation="vertical" className={styles.vbar} />

        {/* Validate */}
        <Button
          type="button"
          variant="ghost"
          className={styles.toolBtn}
          onClick={handleValidatePipeline}
          disabled={!hasCanvasNodes || isValidating}
          aria-keyshortcuts="Meta+E Control+E"
          title="Validate pipeline (⌘E)"
        >
          <MaterialIcon name={validateIconName} size={18} />
          {validateButtonLabel}
        </Button>

        {/* Run */}
        <Button
          type="button"
          variant="ghost"
          className={styles.toolBtn}
          onClick={handleRunPipeline}
          disabled={isRunButtonDisabled}
          title={isValidated ? undefined : 'Validate the pipeline before running'}
        >
          <MaterialIcon name="play_arrow" size={18} />
          {isRunningPipeline ? 'Running...' : 'Run'}
        </Button>

        {/* Stop */}
        <Button
          type="button"
          variant="ghost"
          className={styles.toolBtn}
          onClick={handleStopPipeline}
          disabled={killJobMutation.isPending || !(isRunningPipeline || isInitializingPipeline)}
        >
          <MaterialIcon name="stop_circle" size={18} />
          Stop
        </Button>

        {/* Schedule */}
        <Button
          type="button"
          variant="ghost"
          className={styles.toolBtn}
          onClick={handleSchedulePipeline}
          disabled={isInitializingPipeline || isRunningPipeline || !isRunEnabled}
        >
          <MaterialIcon name="calendar_today" size={18} />
          Schedule
        </Button>

        <div className={styles.spacer} />

        {/* Status pill */}
        <Badge
          variant="outline"
          className={styles.statusPill}
          style={{
            background: pillCfg.pillBg,
            borderColor: pillCfg.pillBorder,
            color: pillCfg.pillText,
          }}
        >
          <span
            className={styles.statusDot}
            style={{
              background: pillCfg.dotColor,
              boxShadow: isSpinning ? `0 0 0 3px ${pillCfg.dotColor}40` : undefined,
              animation: isSpinning ? 'pulseDot 1.4s ease-in-out infinite' : undefined,
            }}
          />
          {pillCfg.label}
        </Badge>

        <Separator orientation="vertical" className={styles.vbar} />

        {/* More menu */}
        <PipelineMoreMenu
          onLockPipeline={onLockPipeline}
          onClearCanvas={onClearCanvas}
          onDeletePipeline={() => { }}
          disableCanvasActions={!hasCanvasNodes}
          canvasLocked={canvasLocked}
        />
      </div>
    </div>
  );
}
